import { useOutletContext } from "react-router-dom";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { UserInfoSubject } from "../components/UserInfo";

/**
 * The page's single identity. Derived from ControlCenter's outlet context, never fetched
 * (research R-001, data-model §2).
 *
 * The **shape** lives on the neutral shared surface as `UserInfoSubject`, because User Manager
 * renders the same panel and needs the same type (Principle II forbids it importing from here).
 * My Info adds one field, `isSubjectTheOperator`, so it passes for a `UserInfoSubject` anywhere.
 *
 * **Resolution deliberately stays here** rather than moving with the type: this hook **throws**
 * when the outlet context is absent, which is right for a page whose subject is implied, while
 * `useUserManagerSubject` must render a per-user failure and leave the list usable (research
 * R-002). Same shape, different failure modes — so the type is shared and the hooks are not.
 */
export interface MyInfoSubject extends UserInfoSubject {
  /**
   * Whether the subject is the person signed in — false while an internal user is acting as someone
   * else. Selects the page's copy register: "your …" only when it really is.
   */
  isSubjectTheOperator: boolean;
}

/*
 * There is deliberately no `accountName` here (FR-004's third header field).
 *
 * The outlet context carries `accountId` but no account name, and the value the header should show
 * — `userinfo`'s `summary.company` — arrives from an async query, whereas this hook resolves
 * synchronously from context. Exposing `accountName: accountId` from here would read as the account
 * name and get rendered as one.
 *
 * So the account line is `IdentityHeader`'s job (T019): read `useGetUserInfo().data.summary.company`
 * and fall back to `subject.accountId` while the query is pending or when `company` is empty — CC9
 * returns `""` rather than null for unset strings, so that fallback is load-bearing.
 *
 * Verified 2026-08-21: `company` matches CC9's authoritative `/serviceAccounts/{accountId}.name`
 * ("ROIMOTORS Auto Group" for `dealerdemo`). But it is a per-user, user-editable profile field and
 * does drift — 11 of 12 sampled `dealerdemo` users matched, while `jlwood2420` read
 * "Marketing Team". Accepted: no new endpoint, and better than showing the raw account id. The
 * durable fix is a BFF endpoint over `/serviceAccounts/{accountId}`, which is out of scope here.
 */

export interface MyInfoOutletContext {
  ccUserInfo?: ControlcenterAPIInterfaceExternal;
}

/**
 * Thrown when the outlet context is absent or carries no identifiers.
 *
 * It MUST NOT be swallowed into a partial subject. The Edge Cases forbid an empty editable form
 * standing in for a missing record — a form with blank fields is indistinguishable from a real
 * profile whose fields are empty, and once Story 2's save is enabled it would turn a failed read
 * into a new record. Failing loudly forces the caller to render a not-found state instead.
 */
export class MyInfoSubjectUnavailableError extends Error {
  constructor(reason: string) {
    super(`My Info subject could not be resolved: ${reason}`);
    this.name = "MyInfoSubjectUnavailableError";
    Object.setPrototypeOf(this, MyInfoSubjectUnavailableError.prototype);
  }
}

const resolveDisplayName = (
  ccUserInfo: ControlcenterAPIInterfaceExternal
): string => {
  const name = [ccUserInfo.first, ccUserInfo.last]
    .filter(part => Boolean(part?.trim()))
    .join(" ")
    .trim();
  // Never render a blank header. CC9 returns "" (not null) for unset strings, so a user with
  // neither name is a real case, not a failure.
  return name || ccUserInfo.userId;
};

/**
 * Resolves the subject of the My Info page.
 *
 * In an "as" context the **identifiers** are the as-user's, not the signed-in internal user's —
 * structurally, not by choice: `PagesControlcenterApp` parameterises `useControlcenterAPIInterface`
 * on the `asAccount`/`asUser` route params and passes the result down as outlet context, so there is
 * exactly one subject object and all four queries key off it (FR-001, FR-001a). `internalUserId`
 * identifies the *authenticated* internal user; it is read only to tell whether the subject is that
 * user (`isSubjectTheOperator`), never as an identifier.
 *
 * ⚠️ **That holds for `userId`/`accountId` only. `displayName` describes whoever is signed in, not
 * the subject** — an upstream defect in shared ControlCenter code, not fixable from this lib.
 * `first`/`last` are copied straight off the session JWT (`ddc-pages-controlcenter-lambda.ts` reads
 * them from `jwtUser.jwtData`, beside `sub`). The handler swaps `userId`/`accountId` to the as-user
 * but passes the name through untouched, so `displayName` is always the operator's.
 * `IdentityHeader` therefore takes the name from `useGetUserInfo().data.summary` instead — see the
 * note there. **Do not reintroduce `subject.displayName` as the header's name.**
 *
 * @throws {MyInfoSubjectUnavailableError} when the context or its identifiers are absent.
 */
export const useMyInfoSubject = (): MyInfoSubject => {
  const context = useOutletContext<MyInfoOutletContext | null>();
  const ccUserInfo = context?.ccUserInfo;

  if (!ccUserInfo) {
    throw new MyInfoSubjectUnavailableError(
      "the ControlCenter outlet context is not available"
    );
  }
  if (!ccUserInfo.userId || !ccUserInfo.accountId) {
    throw new MyInfoSubjectUnavailableError(
      "the outlet context carries no userId/accountId pair"
    );
  }

  return {
    userId: ccUserInfo.userId,
    accountId: ccUserInfo.accountId,
    displayName: resolveDisplayName(ccUserInfo),
    username: ccUserInfo.userId,
    // Unset for a non-internal session; while acting as someone `userId` is the as-user, not it.
    isSubjectTheOperator:
      !ccUserInfo.internalUserId ||
      ccUserInfo.internalUserId === ccUserInfo.userId
  };
};
